import { useQuery } from '@tanstack/react-query';
import { approvalStatusOf, fetchAdminUsers, type AdminUser } from '../../services/adminService';

/** Query key of the approval queue (GET /api/v1/admin/users?approval=pending). */
export const PENDING_APPROVALS_KEY = ['admin', 'users', 'pending'] as const;

export interface PendingApprovals {
  users: AdminUser[];
  /** Number of accounts waiting for approval (0 while loading or on error). */
  count: number;
  isLoading: boolean;
  isError: boolean;
  isRefetching: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * Closed beta: the accounts waiting for an admin's approval. Shared by the
 * "Freigabe ausstehend" section and the count badge on the admin "Users" tab
 * (same query key → one request). Only call it for admins.
 */
export function usePendingApprovals(enabled = true): PendingApprovals {
  const query = useQuery({
    queryKey: PENDING_APPROVALS_KEY,
    queryFn: () => fetchAdminUsers('pending'),
    enabled,
  });
  // Defensive: only rows that really are pending (an older API ignores the filter).
  const listed = query.data?.users ?? [];
  const users = listed.filter((u) => approvalStatusOf(u) === 'pending');
  // More pending accounts than one page lists: the API's total counts them all.
  const total = typeof query.data?.total === 'number' ? query.data.total : 0;
  const count = users.length === listed.length && total > listed.length ? total : users.length;
  return {
    users,
    count,
    isLoading: query.isLoading,
    isError: query.isError,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
}
